import { parsePhoneNumberFromString } from "libphonenumber-js";
import { z } from "zod";
import { ianaTimeZoneSchema } from "./company.js";
import { copilotProposalIdSchema } from "./copilotProposal.js";
import { gradingOutcomeSchema } from "./outcome.js";
import { playbookIdSchema } from "./playbook.js";
import {
  playbookTestAssertionsSchema,
  playbookTestCallSetupSchema,
  playbookTestCustomerMode,
  playbookTestCustomerModeSchema,
  playbookTestCustomerSchema,
  playbookTestOriginSchema,
  playbookTestToolMockSchema
} from "./playbookTestCase.js";
const TeammateAgentBranch = {
  /** Points to the currently live/published revision. */
  Live: "live",
  /** Points to the most recent draft revision. (We may have per-temmate branches in the future.) */
  Draft: "draft"
};
const allTeammateAgentBranches = [
  TeammateAgentBranch.Live,
  TeammateAgentBranch.Draft
];
const teammateAgentBranchSchema = z.enum(allTeammateAgentBranches);
const TeammateAgentKind = {
  /**
   * The prompt is a plain concatenation of user-authored identity sections.
   * No meta-prompt generation and no criteria/section grading post-call.
   */
  Static: "static"
};
const staticTeammateAgentSectionsSchema = z.object({
  agent_name: z.string().trim(),
  agent_role_and_personality: z.string().trim(),
  caller_context: z.string().trim(),
  conversation_flow: z.string().trim(),
  behavior_and_guardrails: z.string().trim(),
  /**
   * How the agent runs a call task it manages (`settings.calling.agent_managed`):
   * the goal, the touches, what to text, when to call, when to stop. Read on
   * the agent's turns between conversations, never on a live call, which is
   * why it is not part of `conversation_flow`. Optional: agents saved before
   * it existed have no key at all.
   */
  outreach_instructions: z.string().trim().optional()
});
const allStaticTeammateAgentSectionKeys = [
  "agent_name",
  "agent_role_and_personality",
  "caller_context",
  "conversation_flow",
  "behavior_and_guardrails",
  "outreach_instructions"
];
const staticAgentInstructionTileKeySchema = z.enum([
  "identity",
  "caller_context",
  "conversation_flow",
  "behavior_and_guardrails",
  "outreach_instructions"
]);
const allStaticAgentInstructionTileKeys = staticAgentInstructionTileKeySchema.options;
const STATIC_AGENT_INSTRUCTION_TILES = {
  identity: {
    label: "Identity and personality",
    sections: ["agent_name", "agent_role_and_personality"]
  },
  caller_context: {
    label: "Customer context",
    sections: ["caller_context"]
  },
  conversation_flow: {
    label: "Voice conversation flow",
    sections: ["conversation_flow"]
  },
  behavior_and_guardrails: {
    label: "General behavior and guardrails",
    sections: ["behavior_and_guardrails"]
  },
  outreach_instructions: {
    label: "Outreach flow",
    sections: ["outreach_instructions"]
  }
};
function resolveStaticAgentInstructionTileKey(section) {
  const tileKey = allStaticAgentInstructionTileKeys.find(
    (key) => STATIC_AGENT_INSTRUCTION_TILES[key].sections.includes(section)
  );
  if (!tileKey) {
    throw new Error(`No instruction tile edits the ${section} section`);
  }
  return tileKey;
}
function compareStaticSectionValuesAsStored(value1, value2) {
  return value1.trim() === value2.trim();
}
function compareStaticSectionsAsStored(sections1, sections2) {
  if (sections1 === null || sections2 === null) {
    return sections1 === sections2;
  }
  return allStaticTeammateAgentSectionKeys.every(
    (key) => compareStaticSectionValuesAsStored(
      sections1[key] ?? "",
      sections2[key] ?? ""
    )
  );
}
const generatedTeammateAgentIdentitySchema = z.object({
  name: z.string().nonempty(),
  role: z.string().nonempty(),
  additional_context: z.string().nonempty(),
  /** Union discriminator; legacy generated identities never set it. */
  kind: z.undefined().optional(),
  /**
   * Static-agent sections staged while a legacy agent is being transitioned to
   * a static agent. Purely staging: the agent keeps running as legacy until
   * these are published (which flips the identity to static, moving them into
   * `sections`).
   */
  pending_static_sections: staticTeammateAgentSectionsSchema.optional()
});
const staticTeammateAgentIdentitySchema = z.object({
  name: z.string(),
  role: z.string(),
  additional_context: z.string(),
  kind: z.literal(TeammateAgentKind.Static),
  sections: staticTeammateAgentSectionsSchema
});
const teammateAgentIdentitySchema = z.discriminatedUnion("kind", [
  staticTeammateAgentIdentitySchema,
  generatedTeammateAgentIdentitySchema
]);
function isStaticTeammateAgentIdentity(identity) {
  return "kind" in identity && identity.kind === TeammateAgentKind.Static;
}
function createEmptyStaticTeammateAgentIdentity() {
  return {
    name: "",
    role: "",
    additional_context: "",
    kind: TeammateAgentKind.Static,
    sections: {
      agent_name: "",
      agent_role_and_personality: "",
      caller_context: "",
      conversation_flow: "",
      behavior_and_guardrails: ""
    }
  };
}
const teammateAgentPostPayloadSchema = z.object({
  /** Internal reference name for this agent */
  name: z.string().nonempty(),
  /** Template key to create the agent from (e.g., "lead_qualification", "after_hours") */
  template_key: z.string().nonempty(),
  /** Agent kind. Omit for a legacy agent with a playbook-generated prompt. */
  kind: z.literal(TeammateAgentKind.Static).optional()
});
const teammateAgentPatchPayloadSchema = z.object({
  name: z.string().nonempty().optional()
});
const callingTimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const callingWindowSchema = z.tuple([callingTimeOfDaySchema, callingTimeOfDaySchema]).refine(([start, end]) => start < end, {
  message: "Calling window start must be before its end"
});
const allCallingWeekdays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
const callingWeekdaySchema = z.enum(allCallingWeekdays);
const MAX_CALLING_RETRY_ATTEMPTS = 10;
const MAX_CALL_TASK_TEXTS = 20;
const DEFAULT_MAX_CALL_TASK_TEXTS = 10;
const DEFAULT_MAX_CALL_TASK_CALLS = 3;
const MIN_CALLING_BACKOFF_MINUTES = 15;
const MAX_CALLING_BACKOFF_MINUTES = 14 * 24 * 60;
const callingRetryPolicySchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("no_retry") }),
  z.object({
    type: z.literal("backoff"),
    max_attempts: z.number().int().positive().max(MAX_CALLING_RETRY_ATTEMPTS),
    /** Delay before each re-dial; the last entry repeats if attempts outrun it. */
    backoff_minutes: z.array(
      z.number().int().min(MIN_CALLING_BACKOFF_MINUTES).max(MAX_CALLING_BACKOFF_MINUTES)
    ).nonempty().max(MAX_CALLING_RETRY_ATTEMPTS)
  })
]);
const MAX_FORWARDING_NUMBER_LENGTH = 32;
const forwardingPhoneNumberSchema = z.string().trim().max(MAX_FORWARDING_NUMBER_LENGTH).transform((value) => {
  const parsed = parsePhoneNumberFromString(value, {
    defaultCountry: "US",
    // The whole string has to be the number, not a number found inside it.
    extract: false
  });
  return parsed?.isPossible() ? String(parsed.number) : value;
});
const MAX_CALLING_WINDOWS_PER_DAY = 8;
const callingHoursSchema = z.partialRecord(
  callingWeekdaySchema,
  z.array(callingWindowSchema).max(MAX_CALLING_WINDOWS_PER_DAY)
);
const MAX_AUTO_ENROLLMENT_PLAYBOOKS = 50;
const MAX_AUTO_ENROLLMENT_OUTCOME_VALUES = 100;
const teammateAgentAutoEnrollmentPlaybookSchema = z.object({
  playbook_id: playbookIdSchema,
  outcome_values: z.array(z.string().trim().min(1)).max(MAX_AUTO_ENROLLMENT_OUTCOME_VALUES)
});
const teammateAgentAutoEnrollmentSchema = z.object({
  /** Empty, or absent altogether, enrolls nobody. */
  playbooks: z.array(teammateAgentAutoEnrollmentPlaybookSchema).max(MAX_AUTO_ENROLLMENT_PLAYBOOKS)
});
const teammateAgentConfigSettingsSchema = z.object({
  calling: z.object({
    /**
     * Falls back to the company timezone when unset. Validated as a real
     * IANA zone: `resolveEffectiveCallTime` passes it straight to luxon, and
     * an unknown zone would throw deep inside the dial path.
     */
    timezone: ianaTimeZoneSchema.optional(),
    hours: callingHoursSchema,
    retry: callingRetryPolicySchema,
    /**
     * The agent runs its own call tasks: on every turn it reads what has
     * happened so far and decides to text, call, wait or finish, following
     * its own instructions. Off, a call task is the deterministic dialer.
     * Set in HQ for now.
     */
    agent_managed: z.boolean(),
    /** Ceilings on what an agent-managed task may do: texts sent and calls
     * placed. Guardrails, not behavior: a correct prompt never reaches them.
     * The retry policy above does not apply to an agent-managed task — what
     * follows a dial is the agent's next turn. */
    max_texts: z.number().int().min(0).max(MAX_CALL_TASK_TEXTS),
    max_calls: z.number().int().min(0).max(MAX_CALLING_RETRY_ATTEMPTS)
  }).partial().optional(),
  inbound: z.object({
    mode: z.enum(["agent", "forward"]),
    /**
     * Where to send inbound calls when mode is "forward". Optional so a
     * half-finished choice still saves; `resolveInboundCallHandling` rejects
     * a forward it can't dial rather than trusting this to be usable.
     */
    forward_to: forwardingPhoneNumberSchema.optional()
  }).optional(),
  /** Absent means the agent enrolls nobody from human calls. */
  auto_enrollment: teammateAgentAutoEnrollmentSchema.optional()
});
function getUnpublishableSettingsReason(settings) {
  const inbound = settings.inbound;
  if (inbound?.mode !== "forward") {
    return null;
  }
  const parsed = inbound.forward_to ? parsePhoneNumberFromString(inbound.forward_to, {
    defaultCountry: "US",
    extract: false
  }) : void 0;
  return parsed?.isValid() && !parsed.ext ? null : "Enter a number inbound calls can be forwarded to.";
}
const DEFAULT_CALLING_HOURS = {
  monday: [["09:00", "17:00"]],
  tuesday: [["09:00", "17:00"]],
  wednesday: [["09:00", "17:00"]],
  thursday: [["09:00", "17:00"]],
  friday: [["09:00", "17:00"]]
};
const DEFAULT_CALLING_RETRY_POLICY = {
  type: "backoff",
  max_attempts: 3,
  backoff_minutes: [240, 1440]
};
const DEFAULT_TEAMMATE_AGENT_CONFIG_SETTINGS = {
  calling: {
    hours: DEFAULT_CALLING_HOURS,
    retry: DEFAULT_CALLING_RETRY_POLICY
  },
  inbound: { mode: "agent" }
};
function resolveTeammateAgentConfigSettings(settings) {
  return {
    calling: {
      ...DEFAULT_TEAMMATE_AGENT_CONFIG_SETTINGS.calling,
      ...settings?.calling
    },
    inbound: settings?.inbound ?? DEFAULT_TEAMMATE_AGENT_CONFIG_SETTINGS.inbound,
    auto_enrollment: settings?.auto_enrollment
  };
}
const teammateAgentConfigUpdatePayloadSchema = z.object({
  identity: teammateAgentIdentitySchema.optional(),
  tool_ids: z.array(z.uuid()).optional(),
  parent_revision_id: z.uuid().optional(),
  settings: teammateAgentConfigSettingsSchema.optional()
});
function serializeTeammateAgentForAdminApi(teammate, revision, extras) {
  if (!teammate.company_id) {
    throw new Error("Expected agent teammate to have a company_id");
  }
  const identity = revision?.identity ?? teammate.teammate_agent_config.identity;
  const toolIds = revision?.instructions.tool_ids ?? teammate.teammate_agent_config.instructions.tool_ids;
  const settings = revision?.settings ?? teammate.teammate_agent_config.settings;
  return {
    id: teammate.id,
    company_id: teammate.company_id,
    name: getTeammateAgentName(teammate),
    teammate_agent_config: {
      playbook_id: teammate.teammate_agent_config.playbook_id,
      identity,
      tool_ids: toolIds,
      phone_number: teammate.teammate_agent_config.phone_number,
      settings: resolveTeammateAgentConfigSettings(settings),
      outbound_calling_enabled: teammate.teammate_agent_config.outbound_calling_enabled,
      live_revision_id: teammate.teammate_agent_config.live_revision_id
    },
    revision_created_at: revision?.created_at,
    teammate_agent_config_revision_id: revision?.id,
    prompt_is_stale: revision?.instructions.prompt_is_stale,
    has_call_tasks: extras?.hasCallTasks
  };
}
function getTeammateAgentName(teammate) {
  if (!teammate.last_name) {
    throw new Error("Expected agent teammate to have a last_name");
  }
  return teammate.last_name;
}
function getAgentIdentityPersonaName(identity, fallbackName) {
  if (isStaticTeammateAgentIdentity(identity)) {
    return identity.sections.agent_name || fallbackName;
  }
  return identity.name;
}
function getTeammateAgentPersonaName(teammate) {
  return getAgentIdentityPersonaName(
    teammate.teammate_agent_config.identity,
    getTeammateAgentName(teammate)
  );
}
function staticSectionsAreEqual(sections1, sections2) {
  if (sections1 === void 0 || sections2 === void 0) {
    return sections1 === sections2;
  }
  return allStaticTeammateAgentSectionKeys.every(
    (key) => sections1[key] === sections2[key]
  );
}
function teammateAgentIdentitiesAreEqual(identity1, identity2) {
  if (isStaticTeammateAgentIdentity(identity1) || isStaticTeammateAgentIdentity(identity2)) {
    return isStaticTeammateAgentIdentity(identity1) && isStaticTeammateAgentIdentity(identity2) && identity1.name === identity2.name && identity1.role === identity2.role && identity1.additional_context === identity2.additional_context && staticSectionsAreEqual(identity1.sections, identity2.sections);
  }
  return identity1.name === identity2.name && identity1.role === identity2.role && identity1.additional_context === identity2.additional_context && staticSectionsAreEqual(
    identity1.pending_static_sections,
    identity2.pending_static_sections
  );
}
function serializePublishedVersionForAdminApi(revision, isCurrentLiveRevision) {
  const publishedAt = revision.metadata.published_at;
  if (!publishedAt) {
    return null;
  }
  return {
    id: revision.id,
    published_at: publishedAt,
    identity: revision.identity,
    is_current: isCurrentLiveRevision
  };
}
const playbookTestSpecExpectedValueSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal("yes"),
  z.literal("no"),
  z.literal("N/A"),
  z.literal("applicable"),
  z.undefined()
]);
const playbookTestSpecExpectedSchema = z.object({
  nodes: z.record(z.uuid(), playbookTestSpecExpectedValueSchema),
  outcomes: gradingOutcomeSchema.optional(),
  // Structural only here: whether an assertion names a tool, an outcome or a
  // custom field definition that exists is checked by the judge that grades it.
  assertions: playbookTestAssertionsSchema.optional()
});
const playbookTestSpecExpectedPayloadSchema = playbookTestSpecExpectedSchema.extend({
  assertions: playbookTestAssertionsSchema.nullable().optional()
});
const playbookTestSpecPayloadFieldsSchema = z.object({
  name: z.string().nonempty(),
  description: z.string().nonempty(),
  expected: playbookTestSpecExpectedPayloadSchema,
  customer_mode: playbookTestCustomerModeSchema.optional(),
  customer: playbookTestCustomerSchema.nullable().optional(),
  call_setup: playbookTestCallSetupSchema.nullable().optional(),
  tool_mocks: z.array(playbookTestToolMockSchema).nullable().optional(),
  origin: playbookTestOriginSchema.nullable().optional()
});
function refinePlaybookTestSpecPayload(payload, ctx) {
  if (payload.customer_mode === playbookTestCustomerMode.Detailed && payload.customer === null) {
    ctx.addIssue({
      code: "custom",
      message: "A detailed test case needs a customer",
      path: ["customer"]
    });
  }
}
const playbookTestSpecPatchPayloadSchema = playbookTestSpecPayloadFieldsSchema.superRefine(
  refinePlaybookTestSpecPayload
);
const playbookTestSpecPostPayloadSchema = playbookTestSpecPayloadFieldsSchema.extend({ proposal_id: copilotProposalIdSchema.optional() }).superRefine(refinePlaybookTestSpecPayload);
function serializePlaybookTestSpecForAdminApi(playbookTestSpec) {
  const { metadata } = playbookTestSpec;
  return {
    id: playbookTestSpec.id,
    company_id: playbookTestSpec.company_id,
    name: playbookTestSpec.name,
    description: metadata.description,
    customer_mode: metadata.customer_mode ?? playbookTestCustomerMode.Simple,
    customer: metadata.customer ?? null,
    call_setup: metadata.call_setup ?? null,
    tool_mocks: metadata.tool_mocks ?? null,
    origin: metadata.origin ?? null,
    expected: playbookTestSpec.expected
  };
}
function readPlaybookTestSpecExpectedOutcome(playbookTestSpec) {
  const { outcomes } = playbookTestSpec.expected;
  const expected = outcomes?.preset ?? outcomes?.custom;
  return (expected && Object.values(expected)[0]?.value) ?? null;
}
const PlaybookTestResultStatus = {
  IN_PROGRESS: "in_progress",
  DONE: "done",
  ERROR: "error"
};
const allPlaybookTestResultStatuses = [
  PlaybookTestResultStatus.IN_PROGRESS,
  PlaybookTestResultStatus.DONE,
  PlaybookTestResultStatus.ERROR
];
const playbookTestResultStatusSchema = z.enum(allPlaybookTestResultStatuses);
function serializePlaybookTestRunForAdminApi(playbookTestRun) {
  return {
    id: playbookTestRun.id,
    company_id: playbookTestRun.company_id,
    teammate_id: playbookTestRun.teammate_id,
    created_at: playbookTestRun.created_at,
    teammate_agent_config_revision_id: playbookTestRun.teammate_agent_config_revision_id,
    playbook_revision_id: playbookTestRun.playbook_revision_id
  };
}
function serializePlaybookTestResultForAdminApi(playbookTestResult, options) {
  return {
    id: playbookTestResult.id,
    company_id: playbookTestResult.company_id,
    playbook_test_run_id: playbookTestResult.playbook_test_run_id,
    playbook_test_spec_id: playbookTestResult.playbook_test_spec_id,
    status: playbookTestResult.status,
    data: options?.includeData ? playbookTestResult.data : void 0,
    output: playbookTestResult.output
  };
}
function serializePlaybookTestSpecResultForAdminApi(playbookTestSpec, playbookTestRun, playbookTestResult) {
  return {
    id: playbookTestSpec.id,
    playbook_test_spec: serializePlaybookTestSpecForAdminApi(playbookTestSpec),
    playbook_test_run: serializePlaybookTestRunForAdminApi(playbookTestRun),
    playbook_test_result: serializePlaybookTestResultForAdminApi(playbookTestResult)
  };
}
const teammateAgentSimulationTurnSchema = z.object({
  /** The same across a conversation's turns; groups their traces into a Langfuse session. */
  conversation_id: z.uuid(),
  messages: z.array(z.unknown()),
  use_live_tool_calls: z.boolean().optional(),
  /** What the agent is told about the call; the client sends it with every turn. */
  call_setup: playbookTestCallSetupSchema.nullable().optional()
});
export {
  DEFAULT_CALLING_HOURS,
  DEFAULT_CALLING_RETRY_POLICY,
  DEFAULT_MAX_CALL_TASK_CALLS,
  DEFAULT_MAX_CALL_TASK_TEXTS,
  DEFAULT_TEAMMATE_AGENT_CONFIG_SETTINGS,
  MAX_AUTO_ENROLLMENT_OUTCOME_VALUES,
  MAX_AUTO_ENROLLMENT_PLAYBOOKS,
  PlaybookTestResultStatus,
  STATIC_AGENT_INSTRUCTION_TILES,
  TeammateAgentBranch,
  TeammateAgentKind,
  allCallingWeekdays,
  allStaticAgentInstructionTileKeys,
  callingHoursSchema,
  callingRetryPolicySchema,
  callingWeekdaySchema,
  compareStaticSectionValuesAsStored,
  compareStaticSectionsAsStored,
  createEmptyStaticTeammateAgentIdentity,
  getAgentIdentityPersonaName,
  getTeammateAgentName,
  getTeammateAgentPersonaName,
  getUnpublishableSettingsReason,
  isStaticTeammateAgentIdentity,
  playbookTestSpecPatchPayloadSchema,
  playbookTestSpecPostPayloadSchema,
  readPlaybookTestSpecExpectedOutcome,
  resolveStaticAgentInstructionTileKey,
  resolveTeammateAgentConfigSettings,
  serializePlaybookTestResultForAdminApi,
  serializePlaybookTestRunForAdminApi,
  serializePlaybookTestSpecForAdminApi,
  serializePlaybookTestSpecResultForAdminApi,
  serializePublishedVersionForAdminApi,
  serializeTeammateAgentForAdminApi,
  staticAgentInstructionTileKeySchema,
  staticTeammateAgentSectionsSchema,
  teammateAgentAutoEnrollmentPlaybookSchema,
  teammateAgentAutoEnrollmentSchema,
  teammateAgentBranchSchema,
  teammateAgentConfigSettingsSchema,
  teammateAgentConfigUpdatePayloadSchema,
  teammateAgentIdentitiesAreEqual,
  teammateAgentPatchPayloadSchema,
  teammateAgentPostPayloadSchema,
  teammateAgentSimulationTurnSchema
};
//# sourceMappingURL=teammateAgent.js.map
